import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Column-width tokens for Split. Storybook options and the catalog enum
 * stay aligned with this list.
 */
export const SPLIT_RATIO_TOKENS = [
  'equal',
  '1/3-2/3',
  '2/3-1/3',
  '1/4-3/4',
  '3/4-1/4',
  '1.3-1',
  '4/9-5/9',
  '5/9-4/9',
] as const;

export type SplitRatio = (typeof SPLIT_RATIO_TOKENS)[number];

export const SPLIT_FLEX_RATIOS = [
  'equal',
  '1/3-2/3',
  '2/3-1/3',
  '1/4-3/4',
  '3/4-1/4',
] as const satisfies readonly SplitRatio[];

/** Ratios rendered as a two-track grid (not flex-basis) once side by side. */
export const SPLIT_GRID_RATIOS = [
  '1.3-1',
  '4/9-5/9',
  '5/9-4/9',
] as const satisfies readonly SplitRatio[];

type SplitGridRatio = (typeof SPLIT_GRID_RATIOS)[number];

const SPLIT_BREAKPOINTS = ['sm', 'md', 'lg', 'xl'] as const;

type SplitBreakpoint = (typeof SPLIT_BREAKPOINTS)[number];

const SPLIT_FLEX_ROW_CLASS = {
  sm: 'sm:flex-row',
  md: 'md:flex-row',
  lg: 'lg:flex-row',
  xl: 'xl:flex-row',
} as const;

const SPLIT_FLEX_ROW_REVERSE_CLASS = {
  sm: 'sm:flex-row-reverse',
  md: 'md:flex-row-reverse',
  lg: 'lg:flex-row-reverse',
  xl: 'xl:flex-row-reverse',
} as const;

/**
 * Both children share row 1 once reversed. A definite column without a row
 * lets sparse auto-placement move the second child to row 2. Shared across
 * every grid ratio so the selector set exists exactly once.
 */
const SPLIT_GRID_REVERSE_PLACEMENT: Record<SplitBreakpoint, string> = {
  sm: 'sm:[&>*:first-child]:col-start-2 sm:[&>*:first-child]:row-start-1 sm:[&>*:not(:first-child)]:col-start-1 sm:[&>*:not(:first-child)]:row-start-1',
  md: 'md:[&>*:first-child]:col-start-2 md:[&>*:first-child]:row-start-1 md:[&>*:not(:first-child)]:col-start-1 md:[&>*:not(:first-child)]:row-start-1',
  lg: 'lg:[&>*:first-child]:col-start-2 lg:[&>*:first-child]:row-start-1 lg:[&>*:not(:first-child)]:col-start-1 lg:[&>*:not(:first-child)]:row-start-1',
  xl: 'xl:[&>*:first-child]:col-start-2 xl:[&>*:first-child]:row-start-1 xl:[&>*:not(:first-child)]:col-start-1 xl:[&>*:not(:first-child)]:row-start-1',
};

/**
 * Forward grid tracks per grid ratio. Each value is a complete literal class
 * string (Tailwind scans source text, so a template-built class name would
 * emit no CSS).
 */
const SPLIT_GRID_TRACKS: Record<
  SplitGridRatio,
  Record<SplitBreakpoint, string>
> = {
  '1.3-1': {
    sm: 'sm:grid sm:grid-cols-split-1-3-1',
    md: 'md:grid md:grid-cols-split-1-3-1',
    lg: 'lg:grid lg:grid-cols-split-1-3-1',
    xl: 'xl:grid xl:grid-cols-split-1-3-1',
  },
  '4/9-5/9': {
    sm: 'sm:grid sm:grid-cols-split-4-9-5-9',
    md: 'md:grid md:grid-cols-split-4-9-5-9',
    lg: 'lg:grid lg:grid-cols-split-4-9-5-9',
    xl: 'xl:grid xl:grid-cols-split-4-9-5-9',
  },
  '5/9-4/9': {
    sm: 'sm:grid sm:grid-cols-split-5-9-4-9',
    md: 'md:grid md:grid-cols-split-5-9-4-9',
    lg: 'lg:grid lg:grid-cols-split-5-9-4-9',
    xl: 'xl:grid xl:grid-cols-split-5-9-4-9',
  },
};

/**
 * Reversed grid tracks per grid ratio, each paired with the shared reverse
 * placement selectors above.
 */
const SPLIT_GRID_REVERSE_TRACKS: Record<
  SplitGridRatio,
  Record<SplitBreakpoint, string>
> = {
  '1.3-1': {
    sm: `sm:grid sm:grid-cols-split-1-3-1-reverse ${SPLIT_GRID_REVERSE_PLACEMENT.sm}`,
    md: `md:grid md:grid-cols-split-1-3-1-reverse ${SPLIT_GRID_REVERSE_PLACEMENT.md}`,
    lg: `lg:grid lg:grid-cols-split-1-3-1-reverse ${SPLIT_GRID_REVERSE_PLACEMENT.lg}`,
    xl: `xl:grid xl:grid-cols-split-1-3-1-reverse ${SPLIT_GRID_REVERSE_PLACEMENT.xl}`,
  },
  '4/9-5/9': {
    sm: `sm:grid sm:grid-cols-split-4-9-5-9-reverse ${SPLIT_GRID_REVERSE_PLACEMENT.sm}`,
    md: `md:grid md:grid-cols-split-4-9-5-9-reverse ${SPLIT_GRID_REVERSE_PLACEMENT.md}`,
    lg: `lg:grid lg:grid-cols-split-4-9-5-9-reverse ${SPLIT_GRID_REVERSE_PLACEMENT.lg}`,
    xl: `xl:grid xl:grid-cols-split-4-9-5-9-reverse ${SPLIT_GRID_REVERSE_PLACEMENT.xl}`,
  },
  '5/9-4/9': {
    sm: `sm:grid sm:grid-cols-split-5-9-4-9-reverse ${SPLIT_GRID_REVERSE_PLACEMENT.sm}`,
    md: `md:grid md:grid-cols-split-5-9-4-9-reverse ${SPLIT_GRID_REVERSE_PLACEMENT.md}`,
    lg: `lg:grid lg:grid-cols-split-5-9-4-9-reverse ${SPLIT_GRID_REVERSE_PLACEMENT.lg}`,
    xl: `xl:grid xl:grid-cols-split-5-9-4-9-reverse ${SPLIT_GRID_REVERSE_PLACEMENT.xl}`,
  },
};

const splitFlexRowCompounds = SPLIT_BREAKPOINTS.flatMap((breakpoint) =>
  SPLIT_FLEX_RATIOS.map((ratio) => ({
    breakpoint,
    ratio,
    class: SPLIT_FLEX_ROW_CLASS[breakpoint],
  })),
);

const splitFlexRowReverseCompounds = SPLIT_BREAKPOINTS.flatMap((breakpoint) =>
  SPLIT_FLEX_RATIOS.map((ratio) => ({
    breakpoint,
    ratio,
    reverse: true as const,
    class: SPLIT_FLEX_ROW_REVERSE_CLASS[breakpoint],
  })),
);

const splitGridTrackCompounds = SPLIT_GRID_RATIOS.flatMap((ratio) =>
  SPLIT_BREAKPOINTS.map((breakpoint) => ({
    breakpoint,
    ratio,
    class: SPLIT_GRID_TRACKS[ratio][breakpoint],
  })),
);

const splitGridTrackReverseCompounds = SPLIT_GRID_RATIOS.flatMap((ratio) =>
  SPLIT_BREAKPOINTS.map((breakpoint) => ({
    breakpoint,
    ratio,
    reverse: true as const,
    class: SPLIT_GRID_REVERSE_TRACKS[ratio][breakpoint],
  })),
);

/**
 * Split variant styles using Class Variance Authority (CVA)
 *
 * Defines gap, breakpoint, ratio, and reverse options for responsive two-column layouts.
 * All styles use design tokens from the HEN Design System.
 *
 * ## Gap Options
 * - xxs through 3xl matching design tokens
 *
 * ## Breakpoint (when to switch from column to row)
 * - sm: 640px, md: 768px, lg: 1024px, xl: 1280px
 *
 * ## Ratio (column width ratios)
 * - equal: 50/50
 * - 1/3-2/3: First column 1/3, second 2/3
 * - 2/3-1/3: First column 2/3, second 1/3
 * - 1/4-3/4: First column 1/4, second 3/4
 * - 3/4-1/4: First column 3/4, second 1/4
 * - 1.3-1: First column 1.3fr, second 1fr
 * - 4/9-5/9: First column 4/9, second 5/9 of the space left after the gap
 * - 5/9-4/9: First column 5/9, second 4/9 of the space left after the gap
 *
 * Flex ratios set basis on the direct children rather than on a wrapper, so
 * the columns size correctly whether the children arrive as JSX siblings or
 * through a slot that renders them from a single element.
 *
 * `1.3-1`, `4/9-5/9`, and `5/9-4/9` cannot be expressed with flex-basis
 * percentages. At the configured breakpoint the container becomes a
 * two-track grid named `split-<ratio>` (e.g. `minmax(0, 1.3fr) minmax(0,
 * 1fr)` for `1.3-1`, `minmax(0, 4fr) minmax(0, 5fr)` for `4/9-5/9`). Below
 * that breakpoint it stays a column. Reverse uses the matching
 * `split-<ratio>-reverse` track and places both children on row 1, with the
 * first child moved into the other track.
 *
 * A sticky column only sticks once the columns sit side by side. Stacked, it
 * would pin over the column below it, so the breakpoint owns the switch.
 */
export const splitVariants = cva('flex flex-col items-stretch [&>*]:min-w-0', {
  variants: {
    gap: {
      xxs: 'gap-xxs',
      xs: 'gap-xs',
      sm: 'gap-sm',
      md: 'gap-md',
      lg: 'gap-lg',
      xl: 'gap-xl',
      '2xl': 'gap-2xl',
      '3xl': 'gap-3xl',
    },
    breakpoint: {
      sm: 'sm:[&>[data-hen-split-sticky]]:sticky sm:[&>[data-hen-split-sticky]]:self-start',
      md: 'md:[&>[data-hen-split-sticky]]:sticky md:[&>[data-hen-split-sticky]]:self-start',
      lg: 'lg:[&>[data-hen-split-sticky]]:sticky lg:[&>[data-hen-split-sticky]]:self-start',
      xl: 'xl:[&>[data-hen-split-sticky]]:sticky xl:[&>[data-hen-split-sticky]]:self-start',
    },
    ratio: {
      equal: '[&>*]:basis-1/2 [&>*]:grow [&>*]:shrink',
      '1/3-2/3':
        '[&>*:first-child]:basis-1/3 [&>*:not(:first-child)]:basis-2/3',
      '2/3-1/3':
        '[&>*:first-child]:basis-2/3 [&>*:not(:first-child)]:basis-1/3',
      '1/4-3/4':
        '[&>*:first-child]:basis-1/4 [&>*:not(:first-child)]:basis-3/4',
      '3/4-1/4':
        '[&>*:first-child]:basis-3/4 [&>*:not(:first-child)]:basis-1/4',
      '1.3-1': '',
      '4/9-5/9': '',
      '5/9-4/9': '',
    } satisfies Record<SplitRatio, string>,
    reverse: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    ...splitFlexRowCompounds,
    ...splitFlexRowReverseCompounds,
    ...splitGridTrackCompounds,
    ...splitGridTrackReverseCompounds,
  ],
  defaultVariants: {
    gap: 'xl',
    breakpoint: 'md',
    ratio: 'equal',
    reverse: false,
  },
});

export type SplitVariantProps = VariantProps<typeof splitVariants>;

export const splitItemVariants = cva('min-w-0 h-full', {
  variants: {
    ratio: {
      equal: 'basis-1/2 grow shrink',
      '1/3-2/3-first': 'basis-1/3',
      '1/3-2/3-second': 'basis-2/3',
      '2/3-1/3-first': 'basis-2/3',
      '2/3-1/3-second': 'basis-1/3',
      '1/4-3/4-first': 'basis-1/4',
      '1/4-3/4-second': 'basis-3/4',
      '3/4-1/4-first': 'basis-3/4',
      '3/4-1/4-second': 'basis-1/4',
    },
  },
  defaultVariants: {
    ratio: 'equal',
  },
});

export type SplitItemVariantProps = VariantProps<typeof splitItemVariants>;

/** Offset for a sticky column. Inert until the parent Split makes it sticky. */
export const splitItemStickyVariants = cva('', {
  variants: {
    spaceAbove: {
      none: 'top-0',
      header: 'top-24',
      sm: 'top-sm',
    },
  },
  defaultVariants: {
    spaceAbove: 'none',
  },
});
